import { z } from 'zod';
import { objectId } from './schemas.js';
/**
 * Money owed to the company: what was invoiced, what has come in, and what is
 * late.
 *
 * The expense half of the books answers "what did we spend". Without this one
 * a workspace can see every penny going out and none coming in, which makes
 * the financial summary a cost report rather than a set of books.
 *
 * An invoice is raised against a CRM customer, and optionally against the deal
 * that won the work — so the pipeline and the receivables are the same story
 * read at two points rather than two systems to reconcile.
 */
/**
 * `overdue` is deliberately *not* here.
 *
 * Being late is a fact about today's date, not a state anything transitions
 * into: a stored `overdue` needs a nightly job to be true, and is wrong every
 * morning until that job runs. It is computed on read instead, from `dueDate`
 * and what is still outstanding.
 */
export const INVOICE_STATUSES = ['draft', 'sent', 'paid', 'void'];
export const PAYMENT_METHODS = ['bank-transfer', 'card', 'cash', 'cheque', 'other'];
const invoiceLine = z.object({
    description: z.string().trim().min(1).max(200),
    quantity: z.number().int().min(1).max(100_000),
    unitAmount: z.number().int().nonnegative().max(1_000_000_000_000),
});
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-16');
export const createInvoiceSchema = z
    .object({
    customerId: objectId,
    dealId: objectId.optional(),
    issueDate: dateOnly,
    dueDate: dateOnly,
    lines: z.array(invoiceLine).min(1).max(50),
    taxRate: z.number().int().min(0).max(10_000).default(0),
    note: z.string().trim().max(2000).optional(),
    terms: z.string().trim().max(500).optional(),
})
    .refine((body) => body.dueDate >= body.issueDate, 'An invoice cannot fall due before it is issued');
/**
 * Only a draft is editable, which the server enforces — an invoice the
 * customer has already been sent is a document that exists outside this
 * system, and quietly rewriting it would leave the two copies disagreeing.
 */
export const updateInvoiceSchema = z
    .object({
    customerId: objectId.optional(),
    issueDate: dateOnly.optional(),
    dueDate: dateOnly.optional(),
    lines: z.array(invoiceLine).min(1).max(50).optional(),
    taxRate: z.number().int().min(0).max(10_000).optional(),
    note: z.string().trim().max(2000).optional(),
    terms: z.string().trim().max(500).optional(),
})
    .refine((body) => body.issueDate === undefined ||
    body.dueDate === undefined ||
    body.dueDate >= body.issueDate, 'An invoice cannot fall due before it is issued');
export const recordPaymentSchema = z.object({
    amount: z.number().int().positive().max(1_000_000_000_000),
    date: dateOnly,
    method: z.enum(PAYMENT_METHODS).default('bank-transfer'),
    reference: z.string().trim().max(120).optional(),
});
export const invoiceQuerySchema = z.object({
    status: z.enum(INVOICE_STATUSES).optional(),
    customerId: objectId.optional(),
    /** The working filter nobody can build from `status` alone. */
    overdue: z.enum(['true']).optional(),
});
export const invoiceSubtotal = (lines) => lines.reduce((sum, line) => sum + line.quantity * line.unitAmount, 0);
/** Tax on the subtotal, rounded to the nearest minor unit at the total level
    rather than per line — a line-by-line rounding drifts by a cent a line. */
export const invoiceTax = (subtotal, taxRateBasisPoints) => Math.round((subtotal * taxRateBasisPoints) / 10_000);
/** How late an invoice is, in whole days, against a `YYYY-MM-DD` today. */
export function daysLate(dueDate, today) {
    const due = Date.parse(`${dueDate}T00:00:00Z`);
    const now = Date.parse(`${today}T00:00:00Z`);
    if (Number.isNaN(due) || Number.isNaN(now))
        return 0;
    return Math.round((now - due) / 86_400_000);
}
/**
 * The aging buckets every receivables report is read through, oldest last.
 * Bounds are inclusive lower, exclusive upper, in days late.
 */
export const AGING_BUCKETS = [
    { key: 'current', label: 'Not yet due', from: -Infinity, to: 1 },
    { key: '1-30', label: '1–30 days', from: 1, to: 31 },
    { key: '31-60', label: '31–60 days', from: 31, to: 61 },
    { key: '61-90', label: '61–90 days', from: 61, to: 91 },
    { key: '90+', label: 'Over 90 days', from: 91, to: Infinity },
];
export const bucketFor = (late) => (AGING_BUCKETS.find((bucket) => late >= bucket.from && late < bucket.to) ?? AGING_BUCKETS[0]).key;
